import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
  Zoom
} from '@mui/material';
import { Title, DetailPopup } from 'shared/components';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Restore as RestoreIcon,
  FileCopy as CloneIcon
} from '@mui/icons-material';
import {
  Content,
  Header,
  Form,
  Bottom,
  Chip,
  Button,
  FormTextFieldContainer
} from './styles';

const DashboardSettingsPanel = ({
  open,
  onSave,
  onClose,
  onClone,
  onDelete,
  onSetDefault,
  board,
  dashboards
}) => {
  const [name, setName] = React.useState(board.name);
  const [changed, setChanged] = React.useState(false);
  const [description, setDescription] = React.useState(board.description);
  const [error, setError] = React.useState('');
  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const [defaultDialog, setDefaultDialog] = React.useState(false);

  React.useEffect(() => {
    setName(board.name);
    setDescription(board.description);
  }, [board]);

  const handleOnChangeName = event => {
    setChanged(true);
    setName(event.target.value);
  };

  const handleOnChangeDescription = event => {
    setChanged(true);
    setDescription(event.target.value);
  };

  const handleClose = () => {
    setChanged(false);
    onClose();
  };

  const handleSave = () => {
    if (!name) {
      return setError('A name is required');
    }
    if (!description) {
      return setError('A description is required');
    }
    const resultNames = dashboards.filter(dash => dash.name === name);
    if (board.name !== name && resultNames.length > 0) {
      return setError(
        'There is already a Dashboard with that name'
      );
    }
    handleClose();
    return onSave(board, name, description);
  };

  const toggleDeleteDialog = () => {
    setDeleteDialog(!deleteDialog);
  };

  const toggleDefaultDialog = () => {
    setDefaultDialog(!defaultDialog);
  };

  const handleDelete = () => {
    toggleDeleteDialog();
    handleClose();
    onDelete(board);
  };

  const handleDiscard = () => {
    setChanged(false);
    setName(board.name);
    setDescription(board.description);
  };

  const handleClone = () => {
    handleClose();
    onClone(board);
  };

  const handleDefault = () => {
    toggleDefaultDialog();
    onSetDefault(board);
    handleClose();
    setChanged(false);
  };

  return (
    <DetailPopup
      open={open}
      onClose={handleClose}
      insideCard={false}
      width="500px"
    >
      <Content>
        <Header>
          <Title>Dashboard Settings</Title>
        </Header>
        <Form component="fieldset">
          <FormTextFieldContainer>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={handleOnChangeName}
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (200 characters)"
                  multiline
                  rows={2}
                  value={description}
                  onChange={handleOnChangeDescription}
                  inputProps={{ maxLength: 200, style: { fontSize: 14 } }}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </FormTextFieldContainer>
        </Form>
        <Bottom>
          {error && (
            <Chip
              icon={<ErrorIcon />}
              label={error}
              color="secondary"
              variant="outlined"
              style={{marginBottom: '20px', width: '100%'}}
            />
          )}
          <Tooltip TransitionComponent={Zoom} title="Save all the changes">
            <Button
              float="right"
              color="primary"
              endIcon={<SaveIcon />}
              onClick={handleSave}
              size="small"
            >
              SAVE
          </Button>
          </Tooltip>
          <Tooltip TransitionComponent={Zoom} title="Clone current dashboard">
            <Button
              variant="outlined"
              float="right"
              endIcon={<CloneIcon />}
              onClick={handleClone}
              size="small"
            >
              CLONE
            </Button>
          </Tooltip>
          {!board.isDefault && (
            <Tooltip
              TransitionComponent={Zoom}
              title="Set this dashboard as default"
            >
              <Button
                variant="outlined"
                float="right"
                endIcon={<RestoreIcon />}
                onClick={toggleDefaultDialog}
                size="small"
              >
                DEFAULT
              </Button>
            </Tooltip>
          )}
          <Tooltip TransitionComponent={Zoom} title="Delete current dashboard">
            <Button
              variant="outlined"
              float="right"
              color="primary"
              endIcon={<DeleteIcon />}
              onClick={toggleDeleteDialog}
              size="small"
            >
              DELETE
            </Button>
          </Tooltip>
          {changed && (
            <Tooltip
              TransitionComponent={Zoom}
              title="Discard all the changes without closing settings"
            >
              <Button
                variant="outlined"
                float="right"
                color="primary"
                endIcon={<DeleteSweepIcon />}
                onClick={handleDiscard}
                size="small"
              >
                DISCARD
              </Button>
            </Tooltip>
          )}
        </Bottom>
      </Content>
      <Dialog open={deleteDialog} onClose={toggleDeleteDialog}>
        <DialogTitle>Continue deleting dashboard?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your Dashboard will be removed, this action is unrecoverable.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={defaultDialog} onClose={toggleDefaultDialog}>
        <DialogTitle>Continue setting your dashboard as default?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This dashboard is gonna be set as default.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleDefaultDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDefault} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </DetailPopup>
  );
};

DashboardSettingsPanel.propTypes = {
  open: PropTypes.bool,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onClone: PropTypes.func,
  onDelete: PropTypes.func,
  onSetDefault: PropTypes.func,
  board: PropTypes.object,
  dashboards: PropTypes.array
};

export default DashboardSettingsPanel;
